import { ReactNode, useCallback, useEffect, useMemo } from 'react';

import { NavigationContext } from './navigation.context';
import { NavigationContextType } from './navigation.types';

import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { StepName } from '~/core/enums';
import { getLocalData } from '~/core/helpers/storageHelper';
import { useRouterTools } from '~/core/hooks/useRouterTools';
import { NavStep } from '~/core/types/navigation';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  resetSmallProjectTicketNumber,
  selectLoadEstimateCode,
} from '~/redux/slices/estimate';
import { resetFocusElement } from '~/redux/slices/layout';
import { addToRouteHistory, selectCurrentRoute } from '~/redux/slices/route';
import { RouteEnum } from '~/redux/slices/route/route.types';
import {
  goToStep,
  isADesignStep,
  selectEnabledSteps,
  selectStepState,
} from '~/redux/slices/stepper';
import {
  selectVisitedSteps,
  updateVisitedSteps,
} from '~/redux/slices/visitedSteps';
import { router, stepRoutes } from '~/router';

export const NavigationContextProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const dispatch = useAppDispatch();

  const { location, navigate } = useRouterTools();

  // Navigation
  const enabledSteps = useAppSelector(selectEnabledSteps);
  const visitedSteps = useAppSelector(selectVisitedSteps);
  const { current: currentStep, next: nextStep } =
    useAppSelector(selectStepState);

  // Routing
  const currentRoute = useAppSelector(selectCurrentRoute);
  const { path: currentRoutePath } =
    currentStep in router
      ? router[currentStep as keyof typeof router]
      : router.designShape;
  const shouldKeepLoadEstimateInURL = getLocalData<boolean>(
    LOCAL_STORAGE_KEY.KEEP_LOAD_ESTIMATE_IN_URL
  );
  const loadEstimateCode = useAppSelector(selectLoadEstimateCode);
  const navSteps: NavStep[] = useMemo(
    () =>
      stepRoutes.map(({ step, label }) => {
        const isDesignSection =
          step === StepName.designShape && isADesignStep(currentStep);

        return {
          stepName: step,
          label: label,
          isCurrent: step === currentStep || isDesignSection,
          isCompleted: visitedSteps.includes(step),
          isEnabled: enabledSteps.includes(step),
        };
      }),
    [currentStep, enabledSteps, visitedSteps]
  );

  const usableURLParams = useMemo(
    () => ({
      [LOCAL_STORAGE_KEY.LOAD_ESTIMATE]: shouldKeepLoadEstimateInURL
        ? loadEstimateCode
        : undefined,
    }),
    [loadEstimateCode, shouldKeepLoadEstimateInURL]
  );

  const navigationUrlPath = useMemo(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    Object.entries(usableURLParams).forEach(([key, value]) => {
      if (value && !params.has(key)) {
        params.append(key, value);
      }
    });

    return `?${params.toString()}`;
  }, [usableURLParams]);

  const goToAndFocusStep = useCallback(
    (step: StepName) => {
      dispatch(goToStep(step));
      dispatch(resetFocusElement());
      // Reset ticket number when switching to another page
      dispatch(resetSmallProjectTicketNumber());
    },
    [dispatch]
  );

  const goToNextStep = useCallback(() => {
    dispatch(goToStep(nextStep));
  }, [dispatch, nextStep]);

  useEffect(() => {
    const resolvedRoute = Object.values(RouteEnum).find(
      (route) => route === currentRoutePath
    );

    if (resolvedRoute && currentRoute !== resolvedRoute) {
      dispatch(addToRouteHistory(resolvedRoute));
    }
    if (
      location.pathname !== currentRoutePath ||
      !String(document.location).includes(navigationUrlPath)
    ) {
      navigate(`${currentRoute}${navigationUrlPath}`);
    }
  }, [
    currentRoutePath,
    currentRoute,
    dispatch,
    location.pathname,
    navigationUrlPath,
    navigate,
  ]);

  useEffect(() => {
    if (!visitedSteps.includes(currentStep)) {
      dispatch(updateVisitedSteps(currentStep));
    }
  }, [currentStep, dispatch, visitedSteps]);

  const contextValue: NavigationContextType = useMemo(
    () => ({
      navSteps,
      enabledSteps,
      nextStep,
      goToAndFocusStep,
      goToNextStep,
    }),
    [enabledSteps, goToNextStep, goToAndFocusStep, navSteps, nextStep]
  );

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};
